import { useCallback } from 'react';

import { noticesStore } from 'stores/global';

import { resolveKeyPoseMotionContext } from '../keyPoses/keyPoseMotionUtils';
import { useStoreApi as useKeyPoseStoreApi } from '../keyPoses/keyPoseStore';
import {
  useStore as useMotionEditorStore,
  useStoreApi as useMotionEditorStoreApi,
} from '../motionEditorStore';
import {
  useStore as useRepathingStore,
  useStoreApi as useRepathingStoreApi,
} from './repathingStore';

export function useRepathingGenerationActions() {
  const motionEditorStore = useMotionEditorStore();
  const motionEditorStoreApi = useMotionEditorStoreApi();
  const keyPoseStoreApi = useKeyPoseStoreApi();
  const repathingStore = useRepathingStore();
  const repathingStoreApi = useRepathingStoreApi();
  const { playback, scene } = motionEditorStore.state;
  const { curve, generation, hasApiKey } = repathingStore.state;
  const motionContext = resolveKeyPoseMotionContext({
    scene,
    playback,
  });
  const canRegeneratePathMotion = Boolean(
    scene.data &&
      hasApiKey &&
      motionContext &&
      curve &&
      generation.status !== 'pending',
  );

  const regeneratePathMotion = useCallback(async () => {
    const currentMotionContext = resolveKeyPoseMotionContext({
      scene: motionEditorStoreApi.state.scene,
      playback: motionEditorStoreApi.state.playback,
    });

    if (!motionEditorStoreApi.state.scene.data) {
      noticesStore.error('Load a scene before regenerating motion.');
      return null;
    }

    if (!currentMotionContext) {
      noticesStore.error('Select a motion before regenerating.');
      return null;
    }

    if (!repathingStoreApi.state.hasApiKey) {
      noticesStore.error('Failed to authenticate. Please try again.');
      return null;
    }

    if (!repathingStoreApi.state.curve) {
      noticesStore.error('Load a path before regenerating.');
      return null;
    }

    const motionDescription =
      motionEditorStoreApi.state.motionDescription.trim() || undefined;
    const result = await repathingStoreApi.regenerateSceneObjectMotionPath({
      sceneID: currentMotionContext.sceneID,
      referenceName: currentMotionContext.referenceName,
      motionID: currentMotionContext.motionID,
      timelineIndex: currentMotionContext.timelineIndex,
      prompt: motionDescription,
    });

    if (!result.ok) {
      noticesStore.error(result.error);
      return null;
    }

    void keyPoseStoreApi.loadSceneObjectMotionEditHistory({
      sceneID: currentMotionContext.sceneID,
      referenceName: currentMotionContext.referenceName,
      motionID: currentMotionContext.motionID,
      timelineIndex: currentMotionContext.timelineIndex,
    });

    return result;
  }, [keyPoseStoreApi, motionEditorStoreApi, repathingStoreApi]);

  return {
    canRegeneratePathMotion,
    regeneratePathMotion,
  };
}
